import { Box, Flex, Separator, SvgIcon } from '@volue/wave-react';
import React from 'react';

import type { LoadFlowScenarioResults } from '@/features/execute-calculation-set/overmind/calculation';
import type { LvLoadFlowSummaryEvaluatedResults } from '@/features/execute-calculation-set/overmind/calculation/derived-state/loadFlowSummary/lowVoltage/LvLoadFlowSummaryEvaluatedResults';
import { countLoadFlowIssuesRequiringAttention } from '@/features/execute-calculation-set/overmind/calculation/evaluation/loadFlow/attentionRequired';
import { useAppState } from '@/overmind';

export type NonEmptyArray<T> = [T, ...T[]];
type LvLoadFlowSummaryCalculationResultsProps = {
  loadFlowScenarios: NonEmptyArray<LoadFlowScenarioResults>;
  loadFlowSummary: LvLoadFlowSummaryEvaluatedResults;
};

export function LvLoadFlowSummaryCalculationResults(
  props: LvLoadFlowSummaryCalculationResultsProps
) {
  const evaluatedGridElements =
    useAppState().calculation.gridElementsDetailsByCalculationType;
  const loadFlowSummary =
    props.loadFlowSummary as LvLoadFlowSummaryEvaluatedResults;
  const attentionStatuses =
    useAppState().calculation.gridElementsAttentionStatuses;
  const resultsBrowsingFilter = useAppState().calculation.tacticResultBrowsingFilter

  const isEvaluated =
    evaluatedGridElements.type === 'EVALUATED' &&
    attentionStatuses.type === 'AVAILABLE';
  return (
    <Box>
      <Box
        role={'heading'}
        css={{
          color: '$colorAccent70',
          fontWeight: 'bold',
          fontSize: 'larger'
        }}
      >
        <Flex main={'space-between'}>
          <LoadFlowSummaryHeader loadFlowScenarios={props.loadFlowScenarios} resultsBrowsingFilter={resultsBrowsingFilter} />
          {isEvaluated && (
            <Box
              data-testid="loadflow-attention-needed-counter"
              css={{ color: '$colorOrange70' }}
            >
              <SvgIcon iconName="warning" className="mRs" />
              {countLoadFlowIssuesRequiringAttention(
                attentionStatuses.byCalculationType
              )}
            </Box>
          )}
        </Flex>
      </Box>
      <Flex flow="column" gap={'spacingXs'}>
        <Flex main={'space-between'}>
          <Box data-testid="loadflow-transformer-loading-header">
            <SvgIcon iconName="activity" className="mRm" />
            {'Starting Voltage primary'}
          </Box>
          <Box data-testid="loadflow-transformer-loading-value">
            {loadFlowSummary.powerTransformerEndsVoltageSummary.primary.min.toString()}
            {
              // TODO: That comparision shouldnt use toString() method
              loadFlowSummary.powerTransformerEndsVoltageSummary.primary.max.toString() !==
                loadFlowSummary.powerTransformerEndsVoltageSummary.primary.min.toString() && (
                <>
                  <Box className="dIB mHxs">{'-'}</Box>
                  <Box className="dIB">
                    {loadFlowSummary.powerTransformerEndsVoltageSummary.primary.max.toString()}
                  </Box>
                </>
              )
            }
          </Box>
        </Flex>
        <Flex main={'space-between'}>
          <Box data-testid="loadflow-transformer-loading-header">
            <SvgIcon iconName="activity" className="mRm" />
            {'Starting Voltage secondary'}
          </Box>
          <Box data-testid="loadflow-transformer-loading-value">
            {loadFlowSummary.powerTransformerEndsVoltageSummary.secondary.min.toString()}
            {
              // TODO: That comparision shouldnt use toString() method
              loadFlowSummary.powerTransformerEndsVoltageSummary.secondary.max.toString() !==
                loadFlowSummary.powerTransformerEndsVoltageSummary.secondary.min.toString() && (
                <>
                  <Box className="dIB mHxs">{'-'}</Box>
                  <Box className="dIB">
                    {loadFlowSummary.powerTransformerEndsVoltageSummary.secondary.max.toString()}
                  </Box>
                </>
              )
            }
          </Box>
        </Flex>
        {loadFlowSummary.powerTransformerEndsVoltageSummary.type ===
          'THREE_WINDING' && (
          <Flex main={'space-between'}>
            <Box data-testid="loadflow-transformer-loading-header">
              <SvgIcon iconName="activity" className="mRm" />
              {'Starting Voltage tertiary'}
            </Box>
            <Box data-testid="loadflow-transformer-loading-value">
              {loadFlowSummary.powerTransformerEndsVoltageSummary.tertiary.min.toString()}
              {
                // TODO: That comparision shouldnt use toString() method
                loadFlowSummary.powerTransformerEndsVoltageSummary.tertiary.max.toString() !==
                  loadFlowSummary.powerTransformerEndsVoltageSummary.tertiary.min.toString() && (
                  <>
                    <Box className="dIB mHxs">{'-'}</Box>
                    <Box className="dIB">
                      {loadFlowSummary.powerTransformerEndsVoltageSummary.tertiary.max.toString()}
                    </Box>
                  </>
                )
              }
            </Box>
          </Flex>
        )}
        <Flex main={'space-between'}>
          <Box data-testid="loadflow-transformer-rated-capacity-header">
            <SvgIcon iconName="building" className="mRm" />
            {'Transformer rated capacity'}
          </Box>
          <Box data-testid="loadflow-transformer-rated-capacity-value">
            {loadFlowSummary.transformerRatedCapacity.value.toString()}
          </Box>
        </Flex>
        <Separator />
        <Flex main={'space-between'}>
          <Box data-testid="loadflow-transformer-loading-header">
            <SvgIcon iconName="building" className="mRm" />
            {'Transformer loading'}
          </Box>
          <Box data-testid="loadflow-transformer-loading-value">
            <Box
              className="dIB"
              css={
                loadFlowSummary.transformerLoading.min.status ===
                'ATTENTION_REQUIRED'
                  ? { color: '$colorRed100', fontWeight: 'bolder ' }
                  : {}
              }
            >
              {loadFlowSummary.transformerLoading.min.value.toString()}
            </Box>
            {
              // TODO: That comparision shouldnt use toString() method
              loadFlowSummary.transformerLoading.max.value.toString() !==
                loadFlowSummary.transformerLoading.min.value.toString() && (
                <>
                  <Box className="dIB mHxs">{'-'}</Box>
                  <Box
                    className="dIB"
                    css={
                      loadFlowSummary.transformerLoading.max.status ===
                      'ATTENTION_REQUIRED'
                        ? { color: '$colorRed100', fontWeight: 'bolder ' }
                        : {}
                    }
                  >
                    {loadFlowSummary.transformerLoading.max.value.toString()}
                  </Box>
                </>
              )
            }
          </Box>
        </Flex>
        <Flex main={'space-between'}>
          <Box data-testid="loadflow-transformer-available-capacity-header">
            <SvgIcon iconName="building" className="mRm" />
            {'Transformer available capacity'}
          </Box>
          <Box data-testid="loadflow-transformer-available-capacity-value">
            <Box
              className="dIB"
              css={
                loadFlowSummary.transformerAvailableCapacity.min.status ===
                'ATTENTION_REQUIRED'
                  ? { color: '$colorRed100', fontWeight: 'bolder ' }
                  : {}
              }
            >
              {loadFlowSummary.transformerAvailableCapacity.min.value.toString()}
            </Box>
            {
              // TODO: That comparision shouldnt use toString() method
              loadFlowSummary.transformerAvailableCapacity.max.value.toString() !==
                loadFlowSummary.transformerAvailableCapacity.min.value.toString() && (
                <>
                  <Box className="dIB mHxs">{'-'}</Box>
                  <Box
                    className="dIB"
                    css={
                      loadFlowSummary.transformerAvailableCapacity.max
                        .status === 'ATTENTION_REQUIRED'
                        ? { color: '$colorRed100', fontWeight: 'bolder ' }
                        : {}
                    }
                  >
                    {loadFlowSummary.transformerAvailableCapacity.max.value.toString()}
                  </Box>
                </>
              )
            }
          </Box>
        </Flex>
        <Flex main={'space-between'}>
          <Box data-testid="loadflow-energy-consumer-voltages-header">
            <SvgIcon iconName="home" className="mRm" />
            {'Energy Consumer voltages'}
          </Box>
          <Box data-testid="loadflow-energy-consumer-voltages-value">
            <Box
              className="dIB"
              css={
                loadFlowSummary.energyConsumerVoltages.min.status ===
                'ATTENTION_REQUIRED'
                  ? { color: '$colorRed100', fontWeight: 'bolder ' }
                  : {}
              }
            >
              {loadFlowSummary.energyConsumerVoltages.min.value.toString()}
            </Box>
            <Box className="dIB mHxs">{'-'}</Box>
            <Box
              className="dIB"
              css={
                loadFlowSummary.energyConsumerVoltages.max.status ===
                'ATTENTION_REQUIRED'
                  ? { color: '$colorRed100', fontWeight: 'bolder ' }
                  : {}
              }
            >
              {loadFlowSummary.energyConsumerVoltages.max.value.toString()}
            </Box>
          </Box>
        </Flex>
        <Flex main={'space-between'}>
          <Box data-testid="loadflow-energy-consumer-voltages-header">
            <SvgIcon iconName="home" className="mRm" />
            {'Energy Consumer voltage drops'}
          </Box>
          <Box data-testid="loadflow-energy-consumer-voltages-value">
            <Box
              className="dIB"
              css={
                loadFlowSummary.energyConsumerVoltageDiffTransformerEndPercent
                  .min.status === 'ATTENTION_REQUIRED'
                  ? { color: '$colorRed100', fontWeight: 'bolder ' }
                  : {}
              }
            >
              {
                // voltage drop should be added and used here instead voltage diif
                // https://github.com/Volue/pg-team-nsa/issues/749
                loadFlowSummary.energyConsumerVoltageDiffTransformerEndPercent.min.value.toString()
              }
            </Box>
            <Box className="dIB mHxs">{'-'}</Box>
            <Box
              className="dIB"
              css={
                loadFlowSummary.energyConsumerVoltageDiffTransformerEndPercent
                  .max.status === 'ATTENTION_REQUIRED'
                  ? { color: '$colorRed100', fontWeight: 'bolder ' }
                  : {}
              }
            >
              {loadFlowSummary.energyConsumerVoltageDiffTransformerEndPercent.max.value.toString()}
              <span>{'%'}</span>
            </Box>
          </Box>
        </Flex>
        <Flex main={'space-between'}>
          <Box data-testid="loadflow-acline-loading-header">
            <SvgIcon iconName="minus" className="mRm" />
            {'ACLine loading'}
          </Box>
          <Box data-testid="loadflow-acline-loading-value">
            <Box
              className="dIB"
              css={
                loadFlowSummary.acLineLoading.min.status ===
                'ATTENTION_REQUIRED'
                  ? { color: '$colorRed100', fontWeight: 'bolder ' }
                  : {}
              }
            >
              {loadFlowSummary.acLineLoading.min.value.toString()}
            </Box>
            <Box className="dIB mHxs">{'-'}</Box>
            <Box
              className="dIB"
              css={
                loadFlowSummary.acLineLoading.max.status ===
                'ATTENTION_REQUIRED'
                  ? { color: '$colorRed100', fontWeight: 'bolder ' }
                  : {}
              }
            >
              {loadFlowSummary.acLineLoading.max.value.toString()}
            </Box>
          </Box>
        </Flex>
        <Separator />
        <Flex main={'space-between'}>
          <Box data-testid="loadflow-energy-consumers-active-power-sum-header">
            <SvgIcon iconName="building" className="mRm" />
            {'Sum Active Power'}
          </Box>
          <Box data-testid="loadflow-energy-consumers-active-power-sum">
            <Box className="dIB">
              {loadFlowSummary.energyConsumersLoadsSummary.activePowers.min.toString()}
            </Box>
            {
              // TODO: That comparision shouldnt use toString() method
              loadFlowSummary.energyConsumersLoadsSummary.activePowers.max.toString() !==
                loadFlowSummary.energyConsumersLoadsSummary.activePowers.min.toString() && (
                <>
                  <Box className="dIB mHxs">{'-'}</Box>
                  <Box className="dIB">
                    {loadFlowSummary.energyConsumersLoadsSummary.activePowers.max.toString()}
                  </Box>
                </>
              )
            }
          </Box>
        </Flex>
        <Flex main={'space-between'}>
          <Box data-testid="loadflow-energy-consumers-reactive-power-sum-header">
            <SvgIcon iconName="building" className="mRm" />
            {'Sum Reactive Power'}
          </Box>
          <Box data-testid="loadflow-energy-consumers-reactive-power-sum">
            <Box className="dIB">
              {loadFlowSummary.energyConsumersLoadsSummary.reactivePowers.min.toString()}
            </Box>

            {
              // TODO: That comparision shouldnt use toString() method
              loadFlowSummary.energyConsumersLoadsSummary.reactivePowers.max.toString() !==
                loadFlowSummary.energyConsumersLoadsSummary.reactivePowers.min.toString() && (
                <>
                  <Box className="dIB mHxs">{'-'}</Box>
                  <Box className="dIB">
                    {loadFlowSummary.energyConsumersLoadsSummary.reactivePowers.max.toString()}
                  </Box>
                </>
              )
            }
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
}

function LoadFlowSummaryHeader(props: { resultsBrowsingFilter: TacticResultBrowsingFilter, loadFlowScenarios: LvLoadFlowSummaryCalculationResultsProps["loadFlowScenarios"] }) {
  return <Box>
    {'LoadFlow'}
    <span data-testid={'loadflow-scenarios-counter'} className="mLs">
      {getScenariosCounter(props.loadFlowScenarios, props.resultsBrowsingFilter)}
    </span>
  </Box>;
}

export function getScenariosCounter(
loadFlowScenarios: NonEmptyArray<LoadFlowScenarioResults>, resultsBrowsingFilter: TacticResultBrowsingFilter) {
  const scenariosCounter = loadFlowScenarios.length;
  if(resultsBrowsingFilter.type === "SELECTED_LOAD_FLOW") {
    if(resultsBrowsingFilter.scenarioIds.length === 1) {
      const scenarioId = resultsBrowsingFilter.scenarioIds[0];
      const scenario = loadFlowScenarios.find(s => s.scenarioId === scenarioId);
      if (scenario) {
        return `(${scenario.scenarioName || "1 scenario"})`;
      }
    } else {
      return `(${scenariosCounter} scenarios)`
    }
  }
  if(resultsBrowsingFilter.type === "ALL") {
    return `(${scenariosCounter} scenarios)`;
  }
  if(resultsBrowsingFilter.type === "ALL_LOAD_FLOW") {
    return `(${scenariosCounter} scenarios)`;
  }

  const scenariosCounterContent =
    scenariosCounter === 1 ? '1 scenario' : `${scenariosCounter} scenarios`;

  return `(${scenariosCounterContent})`;
}
